import { Component, Input } from '@angular/core';

/**
 * Small solid-colour tag holding a short uppercase code — typically the first
 * letters of a mailbox or workspace name — used to tell otherwise-identical
 * rows apart at a glance.
 *
 * Deliberately dumb: both the letters and the colours are computed by the caller
 * (see `getMailboxTagLabel` / `assignMailboxTagColors` in
 * `shared/utils/user-color.util`) so the mapping from a domain object to a colour
 * stays in one place — including the guarantee that no two identities in one list
 * share a tint — and this primitive stays reusable for non-mailbox identities.
 *
 * Attach a `matTooltip` on the host to surface the full identity on hover.
 */
@Component({
  selector: 'ui-initials-tag',
  standalone: true,
  templateUrl: './initials-tag.component.html',
  styleUrls: ['./initials-tag.component.scss'],
})
export class InitialsTagComponent {
  /** Short code to display, already uppercased. */
  @Input() label = '';
  /** Pastel background colour (hex). */
  @Input() color = '';
  /** One step darker than the background, for definition on white surfaces. */
  @Input() borderColor = '';
  /** Dark text of the background's hue. */
  @Input() textColor = '';
  /** `small` matches the card meta row, `medium` the conversation header. */
  @Input() size: 'small' | 'medium' = 'small';
  /**
   * What the code stands for, for assistive tech. Without it the tag is a bare
   * three letters whose meaning lives only in a hover tooltip, which keyboard and
   * screen-reader users never reach.
   */
  @Input() ariaLabel = '';

  get hostClasses(): string {
    return `initials-tag initials-tag--${this.size}`;
  }
}
